import React from 'react';
import { View, Text, StyleSheet, Font } from '@react-pdf/renderer';
import fontBold from './fonts/NotoSansTamil-Bold.ttf';
import fontRegular from './fonts/NotoSansTamil-Regular.ttf';

Font.register({
  family: 'NotoSansTamil',
  fonts: [
    { src: fontRegular, fontWeight: 'normal' },
    { src: fontBold, fontWeight: 'bold' },
  ],
});

const getDynamicPaddingVertical = (theme = {}, itemsOnThisPage, hasPageHeader = false) => {
  if (theme.rowPadding !== undefined) return theme.rowPadding;

  const N = itemsOnThisPage || theme.productsPerPage || 38;
  const usableHeight = hasPageHeader ? 580 : 710;
  const targetRowHeight = usableHeight / Math.max(N, 1);

  const baseFont = theme.fontSize || 10;
  const calculatedPadding = (targetRowHeight - baseFont - 2) / 2;

  return Math.max(0.5, Math.min(5.0, calculatedPadding));
};

const createStyles = (theme = {}, itemsOnThisPage, hasPageHeader = false) => {
  const paddingVert = getDynamicPaddingVertical(theme, itemsOnThisPage, hasPageHeader);
  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      fontFamily: 'NotoSansTamil',
    },
    normalText: {
      fontFamily: 'NotoSansTamil',
      fontWeight: 'normal',
    },
    w100: { width: '100%' },
    borderTop: { borderTop: `1px solid ${theme.bordercolor || 'black'}`, borderColor: theme.bordercolor || 'black' },
    borderRight: { borderRight: `1px solid ${theme.bordercolor || 'black'}`, borderColor: theme.bordercolor || 'black' },
    borderBottom: { borderBottom: `1px solid ${theme.bordercolor || 'black'}`, borderColor: theme.bordercolor || 'black' },
    textCenter: {
      fontFamily: 'NotoSansTamil',
      textAlign: 'center',
      fontSize: theme.fontSize || 10,
    },
    textLeft: {
      fontFamily: 'NotoSansTamil',
      textAlign: 'left',
      fontSize: theme.fontSize || 10,
    },
    paddingRow3: {
      paddingVertical: paddingVert,
      paddingHorizontal: 2,
    },
    headerBg: {
      backgroundColor: theme.headerbackground || 'white',
    },
    rowBg: {
      backgroundColor:
        theme?.tableBg && theme?.tableBg !== 'white' && theme?.tableBg !== '#ffffff'
          ? theme.tableBg
          : 'transparent',
    },
    headerText: {
      fontFamily: 'NotoSansTamil',
      color: theme.headertext || '#fff',
      fontWeight: 'bold',
    },
    tamilText: {
      fontFamily: 'NotoSansTamil',
      fontWeight: 'normal',
      fontSize: theme.fontSize || 10,
    },
    englishText: {
      fontFamily: 'NotoSansTamil',
      fontSize: theme.fontSize || 10,
      textAlign: 'left',
    },
  });
};

const CardFour = ({
  sno,
  product,
  productTa,
  netrate,
  per,
  discountrate,
  Rs,
  Ps,
  isHeader = false,
  isGroupItem = false,
  theme = {},
  itemsOnThisPage,
  hasPageHeader = false,
}) => {
  const styles = createStyles(theme, itemsOnThisPage, hasPageHeader);
  const isAltRow = Boolean(theme?.enableAltRow && Number(sno) % 2 === 0);
  const conditionalTopBorder = isHeader ? styles.borderTop : {};
  const conditionalBg = isHeader
    ? styles.headerBg
    : isAltRow && theme?.altTableBg
    ? { backgroundColor: theme.altTableBg }
    : styles.rowBg;
  const conditionalTextColor = isHeader ? styles.headerText : {};

  const hideDiscountRate = Boolean(theme?.hideDiscountRate);
  const hideRsPs = Boolean(theme?.hideRsPs);
  const showRequirement = Boolean(theme?.showRequirement);

  const separateTamilCol = Boolean(theme?.enableSeparateTamilColumn || (hideDiscountRate && theme?.enableSeparateTamilColumn !== false));

  // Dynamic font size calculation based on text length to auto-adjust cell size
  const getDynamicFontSize = (textEn = '', textTa = '') => {
    const baseSize = theme.fontSize || 10;
    if (isHeader) return baseSize;

    const enLen = textEn ? textEn.length : 0;
    const taLen = textTa ? textTa.length : 0;

    if (separateTamilCol) {
      // In separate column mode, each text occupies its own column
      const maxLen = Math.max(enLen, taLen);
      if (maxLen > 40) return Math.min(baseSize, 7.5);
      if (maxLen > 28) return Math.min(baseSize, 8.5);
      if (maxLen > 20) return Math.min(baseSize, 9.5);
      return baseSize;
    } else {
      // Combined in single column
      const totalLen = enLen + taLen;
      const maxLen = Math.max(enLen, taLen);

      if (totalLen > 70 || maxLen > 38) return Math.min(baseSize, 7.5);
      if (totalLen > 50 || maxLen > 28) return Math.min(baseSize, 8.5);
      if (totalLen > 35 || maxLen > 20) return Math.min(baseSize, 9.5);
      return baseSize;
    }
  };

  const dynamicFontSize = getDynamicFontSize(product, productTa);

  // Dynamic column width calculation
  let snoWidth = '5%';
  let productWidth = '55%';
  let tamilWidth = '0%';
  let netRateWidth = '10%';
  let perWidth = '8%';
  let discountRateWidth = '10%';
  let rsWidth = '6%';
  let psWidth = '6%';
  let requirementWidth = '12%';

  if (separateTamilCol) {
    if (showRequirement) {
      if (hideDiscountRate) {
        snoWidth = '5%';
        productWidth = '31%';
        tamilWidth = '31%';
        netRateWidth = '10%';
        perWidth = '7%';
        requirementWidth = '16%';
      } else {
        snoWidth = '5%';
        productWidth = '27%';
        tamilWidth = '27%';
        netRateWidth = '10%';
        perWidth = '7%';
        discountRateWidth = '10%';
        requirementWidth = '14%';
      }
    } else if (hideDiscountRate && hideRsPs) {
      snoWidth = '6%';
      productWidth = '38%';
      tamilWidth = '38%';
      netRateWidth = '10%';
      perWidth = '8%';
    } else if (hideDiscountRate) {
      snoWidth = '5%';
      productWidth = '31%';
      tamilWidth = '31%';
      netRateWidth = '10%';
      perWidth = '7%';
      rsWidth = '8%';
      psWidth = '8%';
    } else if (hideRsPs) {
      snoWidth = '5%';
      productWidth = '34%';
      tamilWidth = '34%';
      netRateWidth = '10%';
      perWidth = '7%';
      discountRateWidth = '10%';
    } else {
      // Full standard columns + Separate Tamil Column
      snoWidth = '5%';
      productWidth = '27%';
      tamilWidth = '27%';
      netRateWidth = '10%';
      perWidth = '7%';
      discountRateWidth = '10%';
      rsWidth = '7%';
      psWidth = '7%';
    }
  } else {
    // Single product column (English + Tamil together)
    if (showRequirement) {
      if (hideDiscountRate) {
        snoWidth = '5%';
        productWidth = '60%';
        netRateWidth = '10%';
        perWidth = '9%';
        requirementWidth = '16%';
      } else {
        snoWidth = '5%';
        productWidth = '55%';
        netRateWidth = '10%';
        perWidth = '8%';
        discountRateWidth = '10%';
        requirementWidth = '12%';
      }
    } else if (hideDiscountRate && hideRsPs) {
      snoWidth = '6%';
      productWidth = '76%';
      netRateWidth = '10%';
      perWidth = '8%';
    } else if (hideDiscountRate) {
      snoWidth = '5%';
      productWidth = '62%';
      netRateWidth = '10%';
      perWidth = '7%';
      rsWidth = '8%';
      psWidth = '8%';
    } else if (hideRsPs) {
      snoWidth = '6%';
      productWidth = '65%';
      netRateWidth = '11%';
      perWidth = '8%';
      discountRateWidth = '10%';
    }
  }

  // --- For Grouped Items: Only show 3 cols ---
  if (isGroupItem) {
    return (
      <View style={[styles.row, styles.w100]}>
        <View
          style={[
            { width: '10%' },
            conditionalTopBorder,
            styles.borderRight,
            styles.borderBottom,
            styles.paddingRow3,
            conditionalBg,
          ]}
        >
          <Text style={[styles.textCenter, conditionalTextColor, isHeader ? { fontWeight: 'bold' } : {}]}>{sno}</Text>
        </View>

        <View
          style={[
            { width: '70%' },
            conditionalTopBorder,
            styles.borderRight,
            styles.borderBottom,
            styles.paddingRow3,
            conditionalBg,
            conditionalTextColor,
          ]}
        >
          {isHeader ? (
            <Text style={[styles.textCenter, conditionalTextColor, { fontWeight: 'bold' }]}>{product}</Text>
          ) : (
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <View style={{ flex: 1, paddingRight: 2, justifyContent: 'center' }}>
                <Text style={[styles.textLeft, conditionalTextColor, { fontSize: dynamicFontSize }]}>{product}</Text>
              </View>
              {productTa ? (
                <View style={{ flex: 1, paddingLeft: 2, justifyContent: 'center' }}>
                  <Text style={[styles.tamilText, conditionalTextColor, { fontSize: dynamicFontSize, textAlign: 'right' }]}>{productTa}</Text>
                </View>
              ) : null}
            </View>
          )}
        </View>

        <View
          style={[
            { width: '20%' },
            conditionalTopBorder,
            styles.borderBottom,
            styles.paddingRow3,
            conditionalBg,
          ]}
        >
          <Text style={[styles.textCenter, conditionalTextColor, isHeader ? { fontWeight: 'bold' } : {}]}>{per}</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.row, styles.w100]}>
      {/* S.No */}
      <View
        style={[
          { width: snoWidth },
          conditionalTopBorder,
          styles.borderRight,
          styles.borderBottom,
          styles.paddingRow3,
          conditionalBg,
        ]}
      >
        <Text style={[styles.textCenter, conditionalTextColor, isHeader ? { fontWeight: 'bold' } : {}]}>{sno}</Text>
      </View>

      {/* English Product Name */}
      <View
        style={[
          { width: productWidth },
          conditionalTopBorder,
          styles.borderRight,
          styles.borderBottom,
          styles.paddingRow3,
          conditionalBg,
          conditionalTextColor,
          { justifyContent: 'center' },
        ]}
      >
        {isHeader ? (
          <Text style={[styles.textCenter, conditionalTextColor, { fontWeight: 'bold', width: '100%' }]}>
            {product}
          </Text>
        ) : !separateTamilCol && productTa ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <View style={{ width: '49%', paddingRight: 2, justifyContent: 'center' }}>
              <Text style={[styles.textLeft, conditionalTextColor, { fontSize: dynamicFontSize, textAlign: 'left' }]}>
                {product}
              </Text>
            </View>
            <View style={{ width: '49%', paddingLeft: 2, justifyContent: 'center' }}>
              <Text style={[styles.tamilText, conditionalTextColor, { fontSize: dynamicFontSize, textAlign: 'right' }]}>
                {productTa}
              </Text>
            </View>
          </View>
        ) : (
          <Text style={[styles.textLeft, conditionalTextColor, { fontSize: dynamicFontSize }]}>
            {product}
          </Text>
        )}
      </View>

      {/* Dedicated Separate Tamil Column */}
      {separateTamilCol && (
        <View
          style={[
            { width: tamilWidth },
            conditionalTopBorder,
            styles.borderRight,
            styles.borderBottom,
            styles.paddingRow3,
            conditionalBg,
            conditionalTextColor,
            { justifyContent: 'center', alignItems: 'center' },
          ]}
        >
          <Text style={[styles.textCenter, conditionalTextColor, { fontSize: isHeader ? (theme.fontSize || 10) : dynamicFontSize, fontWeight: isHeader ? 'bold' : 'normal' }]}>
            {isHeader ? 'பொருள் பெயர்' : productTa || ''}
          </Text>
        </View>
      )}

      {/* Net Rate */}
      <View
        style={[
          { width: netRateWidth },
          conditionalTopBorder,
          styles.borderRight,
          styles.borderBottom,
          styles.paddingRow3,
          conditionalBg,
        ]}
      >
        <Text style={[styles.textCenter, conditionalTextColor, isHeader ? { fontWeight: 'bold' } : {}]}>
          {isHeader ? netrate : netrate ? `₹${netrate}` : ''}
        </Text>
      </View>

      {/* Per */}
      <View
        style={[
          { width: perWidth },
          conditionalTopBorder,
          !hideDiscountRate || !hideRsPs || showRequirement ? styles.borderRight : {},
          styles.borderBottom,
          styles.paddingRow3,
          conditionalBg,
        ]}
      >
        <Text style={[styles.textCenter, conditionalTextColor, isHeader ? { fontWeight: 'bold' } : {}]}>{per}</Text>
      </View>

      {/* Discount Rate (Hidden if hideDiscountRate is true) */}
      {!hideDiscountRate && (
        <View
          style={[
            { width: discountRateWidth },
            conditionalTopBorder,
            !hideRsPs || showRequirement ? styles.borderRight : {},
            styles.borderBottom,
            styles.paddingRow3,
            conditionalBg,
          ]}
        >
          <Text style={[styles.textCenter, conditionalTextColor, isHeader ? { fontWeight: 'bold' } : {}]}>
            {isHeader ? discountrate : discountrate ? `₹${discountrate}` : ''}
          </Text>
        </View>
      )}

      {/* Single Merged Requirement Column OR Separate Rs & Ps Columns */}
      {showRequirement ? (
        <View
          style={[
            { width: requirementWidth },
            conditionalTopBorder,
            styles.borderBottom,
            styles.paddingRow3,
            conditionalBg,
            { justifyContent: 'center', alignItems: 'center' },
          ]}
        >
          <Text style={[styles.textCenter, conditionalTextColor, isHeader ? { fontWeight: 'bold' } : {}]}>
            {isHeader ? 'Requirement' : ''}
          </Text>
        </View>
      ) : (
        <>
          {/* Rs (Hidden if hideRsPs is true) */}
          {!hideRsPs && (
            <View
              style={[
                { width: rsWidth },
                conditionalTopBorder,
                styles.borderRight,
                styles.borderBottom,
                styles.paddingRow3,
                conditionalBg,
              ]}
            >
              <Text style={[styles.textCenter, conditionalTextColor, isHeader ? { fontWeight: 'bold' } : {}]}>{Rs}</Text>
            </View>
          )}

          {/* Ps (Hidden if hideRsPs is true) */}
          {!hideRsPs && (
            <View
              style={[
                { width: psWidth },
                conditionalTopBorder,
                styles.borderBottom,
                styles.paddingRow3,
                conditionalBg,
              ]}
            >
              <Text style={[styles.textCenter, conditionalTextColor, isHeader ? { fontWeight: 'bold' } : {}]}>{Ps}</Text>
            </View>
          )}
        </>
      )}
    </View>
  );
};

export default CardFour;